'use client'

import { toast } from '@/components/common/toast'
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form'
import { __cAuthService } from '@/services/client/__c.auth.service'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useBoolean } from 'usehooks-ts'
import * as z from 'zod'
import { Button } from '../ui/button'
import { Input } from '../ui/input'

import { OtpForm } from '@/components/auth/otp-form'
import { ImageCM } from '@/components/extension/image-cm'
import { Divider } from '@/components/ui/divider'
import {
  useRefreshAfterLogin,
  useUserQuery,
} from '@/hooks/react-query/user/use-user'

import { useTrackEvent } from '@/lib/gtm/track-event'
import { ETrackingEvent } from '@/lib/gtm/type'
import { getValue, validatePhone } from '@/lib/utils'
import { hidePopup, showPopup } from '@/providers/popup-provider'
import { RANKING_LABEL_VN } from '@/types/auth'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { ForgotPasswordForm } from './forgot-password-form'
import { LoginForm } from './login-form'

export function RegisterForm({
  isPage,
  callback,
}: {
  isPage?: boolean
  callback?: () => void
}) {
  const { refetch: refetchUser } = useUserQuery()
  const loading = useBoolean()
  const router = useRouter()
  const trackEvent = useTrackEvent()
  const refreshAfterLogin = useRefreshAfterLogin()
  const t = useTranslations('login')

  const formSchema = z.object({
    fullname: z.string().min(6, t('required_fullname')),
    phone: z.string().refine(
      (value) => {
        const phoneValidate = validatePhone(value)
        return phoneValidate.valid
      },
      {
        message: t('invalid_phone'),
      }
    ),
    email: z.string().email(t('invalid_email')),
    password: z.string().min(6, t('password_too_short')),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
      fullname: '',
      phone: '',
    },
  })

  async function onSubmit(values: z.infer<typeof formSchema>) {
    loading.setTrue()
    try {
      const res = await __cAuthService.register(values)

      if (res?.status && res.status === 200) {
        toast.message({
          title: t('register_success'),
          message: res.message,
        })

        // Get user ID from response
        const registeredUserId = getValue(res, 'data.user_id', '')
        if (registeredUserId) {
          // Sử dụng showPopup trực tiếp với OtpForm thay vì qua showOTPModal
          setTimeout(() => {
            showPopup(
              <OtpForm
                phone={values.phone}
                onSuccess={processAfterLoginSuccess}
                type='register'
                isAuth={true}
                bodyAuth={{
                  user_id: registeredUserId,
                  fullname: values.fullname,
                  email: values.email,
                  phone: values.phone,
                }}
                onClose={() => hidePopup()}
              />
            )
          }, 100)
        }
      } else {
        toast.message({
          title: t('register_fail'),
          message: res.message,
        })
      }
    } catch (error) {
      console.error(error)
    } finally {
      loading.setFalse()
    }
  }

  const processAfterLoginSuccess = async () => {
    refreshAfterLogin()

    const { data: user } = await refetchUser()

    if (user) {
      trackEvent({
        event: ETrackingEvent.UserRegister,
        user_id: user.user_id,
        user_type: RANKING_LABEL_VN[user?.ranking?.ranking_id],
      })
    }
    callback?.()
    hidePopup()
  }

  return (
    <>
      <div className='text-neutral-800'>
        <ImageCM src={'/March2024/mceclip4_81.jpg'} width={132} height={22} />
        <h3 className='mt-4 font-criteria text-[28px] font-medium leading-8 tracking-normal text-neutral-900'>
          {t('register_privileges')}
        </h3>
        <p className='mt-3.5 font-sans text-xs'>{t('register_benefit')}</p>
        <div className='mb-4 mt-1 grid grid-cols-3 gap-3'>
          <ImageCM
            src={'/March2024/mceclip3_52.jpg'}
            width={112}
            height={44}
            className='w-full object-cover'
            alt={t('voucher_alt')}
          />
          <ImageCM
            src={'/March2024/mceclip1_36.jpg'}
            width={112}
            height={44}
            className='w-full object-cover'
            alt={t('gift_alt')}
          />
          <ImageCM
            src={'/March2024/mceclip2_55.jpg'}
            width={112}
            height={44}
            className='w-full object-cover'
            alt={t('cashback_alt')}
          />
        </div>
        <p className='mb-3 mt-4 font-sans text-xs font-bold'>
          {t('login_or_register_free')}
        </p>
        <div className='flex gap-3'>
          <Button
            variant={'outline'}
            size={'icon'}
            className='rounded-lg p-1.5 hover:bg-neutral-50 active:bg-neutral-50'
          >
            <ImageCM
              src={'/September2023/mceclip1_21.png'}
              width={26}
              height={26}
              className='h-full w-full object-scale-down'
              alt='Login google'
            />
          </Button>
          <Button
            variant={'outline'}
            size={'icon'}
            className='rounded-lg p-1.5 hover:bg-neutral-50 active:bg-neutral-50'
          >
            <ImageCM
              src={'/September2023/mceclip0_86.png'}
              width={26}
              height={26}
              className='h-full w-full object-scale-down'
              alt='Login facebook'
            />
          </Button>
        </div>
        <Divider text={t('or')} />
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
          <div className='flex items-start justify-between gap-3'>
            <FormField
              control={form.control}
              name='fullname'
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormControl>
                      <Input
                        autoFocus
                        floating
                        label={t('fullname')}
                        {...field}
                        error={form.formState.errors?.fullname}
                      />
                    </FormControl>
                  </FormItem>
                )
              }}
            />
            <FormField
              control={form.control}
              name='phone'
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormControl>
                      <Input
                        floating
                        label={t('phone')}
                        {...field}
                        error={form.formState.errors?.phone}
                      />
                    </FormControl>
                  </FormItem>
                )
              }}
            />
          </div>
          <FormField
            control={form.control}
            name='email'
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl>
                    <Input
                      floating
                      label={t('email')}
                      {...field}
                      error={form.formState.errors?.email}
                    />
                  </FormControl>
                </FormItem>
              )
            }}
          />
          <FormField
            control={form.control}
            name='password'
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    floating
                    type='password'
                    {...field}
                    label={t('password')}
                    error={form.formState.errors?.password}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <Button
            type='submit'
            className='w-full'
            variant='black'
            loading={loading.value}
          >
            {t('register_account')}
          </Button>
        </form>
      </Form>

      <div className='mt-4 flex justify-between'>
        <Button
          variant='flat'
          className='h-fit p-0 font-sans text-xs font-medium normal-case'
          onClick={() => {
            if (isPage) {
              router.push('/login')
            } else {
              showPopup(<LoginForm />)
            }
          }}
        >
          {t('login')}
        </Button>

        <Button
          variant='flat'
          className='h-fit p-0 font-sans text-xs font-medium normal-case'
          onClick={() => {
            if (isPage) {
              router.push('/forgot-password')
            } else {
              showPopup(<ForgotPasswordForm />)
            }
          }}
        >
          {t('forgot_password')}
        </Button>
      </div>
    </>
  )
}
